import {
  adminAbuseReportListResponseSchema,
  cancellationRequestListResponseSchema,
  resolveCancellationRequestSchema,
  type ResolveCancellationRequest,
  activityLogListResponseSchema,
  activityLogQuerySchema,
  addMemberRequestSchema,
  memberSchema,
  type AddMemberRequest,
  createOrgAddressRequestSchema,
  orgAddressListResponseSchema,
  orgAddressSchema,
  updateOrgAddressRequestSchema,
  type CreateOrgAddressRequest,
  type UpdateOrgAddressRequest,
  adminClaimInvitationListQuerySchema,
  adminClaimInvitationListResponseSchema,
  adminCreateClaimInvitationRequestSchema,
  adminCreateShadowOrgRequestSchema,
  adminDashboardResponseSchema,
  adminAnalyticsRangeQuerySchema,
  adminAnalyticsSummarySchema,
  adminAnalyticsTimeseriesSchema,
  adminAnalyticsFunnelSchema,
  adminAnalyticsRevenueSchema,
  type AdminAnalyticsRangeQuery,
  adminImportPostingsRequestSchema,
  adminImportPostingsResponseSchema,
  adminHideRatingRequestSchema,
  adminKycListQuerySchema,
  adminOrgKycListQuerySchema,
  adminOrgKycListResponseSchema,
  adminOverrideVerifyLevelRequestSchema,
  type AdminOrgKycListQuery,
  type AdminOverrideVerifyLevelRequest,
  adminOrgListQuerySchema,
  adminOrgListItemSchema,
  adminOrgListResponseSchema,
  adminPostingDetailSchema,
  adminPostingListQuerySchema,
  adminPostingListResponseSchema,
  adminResolveAbuseRequestSchema,
  adminRevokeSessionResponseSchema,
  adminSessionListResponseSchema,
  adminUserContactResponseSchema,
  adminCreditPurchaseListQuerySchema,
  adminCreditPurchaseListResponseSchema,
  adminCreditPurchaseSummaryQuerySchema,
  adminCreditPurchaseSummarySchema,
  adminCreditPurchaseDetailSchema,
  adminCreditPurchaseActionResponseSchema,
  adminCreditPurchaseMarkPaidRequestSchema,
  adminInviteListQuerySchema,
  adminInviteListResponseSchema,
  type AdminInviteListQuery,
  adminSuspendUserRequestSchema,
  adminTodoCountsSchema,
  adminShadowOrgsWithApplicantsQuerySchema,
  adminShadowOrgsWithApplicantsResponseSchema,
  adminAssignableShadowOrgsQuerySchema,
  adminAssignableShadowOrgListResponseSchema,
  adminGrantOrgRequestSchema,
  adminGrantOrgResponseSchema,
  adminUserDetailSchema,
  adminUserMutationResponseSchema,
  adminUsersListResponseSchema,
  adminUsersQuerySchema,
  adminNotificationTemplateListResponseSchema,
  adminSendNotificationRequestSchema,
  adminSendNotificationResponseSchema,
  adminSystemPostingTemplateUpsertSchema,
  adminUpdateNotificationTemplateRequestSchema,
  postingTemplateListResponseSchema,
  postingTemplateSchema,
  type AdminSystemPostingTemplateUpsertRequest,
  adminWorkerKycListResponseSchema,
  adminIdentityListResponseSchema,
  userIdentityAdminSchema,
  identityRejectRequestSchema,
  type IdentityRejectRequest,
  notificationTemplateSchema,
  applicantListResponseSchema,
  applicantProfileSchema,
  applicationSchema,
  createWorkerExperienceRequestSchema,
  patchMeWorkerRequestSchema,
  userMeSchema,
  workerExperienceSchema,
  claimAcceptRequestSchema,
  claimAcceptResponseSchema,
  claimPreviewResponseSchema,
  adminCreatePostingRequestSchema,
  workerKycSubmissionAdminSchema,
  orgClaimInvitationListResponseSchema,
  orgClaimInvitationSchema,
  orgImagePresignRequestSchema,
  orgImagePresignResponseSchema,
  orgKycSubmissionAdminSchema,
  orgSchema,
  postingSchema,
  kycRejectRequestSchema,
  ratingSchema,
  inviteApplicationResponseSchema,
  interviewCalendarQuerySchema,
  interviewCalendarResponseSchema,
  type InterviewCalendarQuery,
  creditPackListResponseSchema,
  creditPackSchema,
  creditPackUpsertRequestSchema,
  adminSubscriptionPlanUpsertRequestSchema,
  adminSubscriptionPlanListResponseSchema,
  adminPlanPurchaseListQuerySchema,
  adminPlanPurchaseListResponseSchema,
  adminSubscriptionStatsSchema,
  adminProSourceOrgsResponseSchema,
  subscriptionPlanSchema,
  orgPlanStatusSchema,
  adminGiftCodeListQuerySchema,
  adminGiftCodeListResponseSchema,
  adminCreateGiftCodesRequestSchema,
  adminCreateGiftCodesResponseSchema,
  planGiftCodeSchema,
  updateOrgRequestSchema,
  adminUpdatePostingRequestSchema,
  type ActivityLogQuery,
  type AdminClaimInvitationListQuery,
  type AdminCreateClaimInvitationRequest,
  type AdminCreateShadowOrgRequest,
  type AdminImportPostingsRequest,
  type AdminHideRatingRequest,
  type AdminKycListQuery,
  type AdminOrgListQuery,
  type AdminShadowOrgsWithApplicantsQuery,
  type AdminAssignableShadowOrgsQuery,
  type AdminGrantOrgRequest,
  type AdminPostingListQuery,
  type AdminResolveAbuseRequest,
  type AdminSendNotificationRequest,
  type AdminUpdateNotificationTemplateRequest,
  type NotificationTypeValue,
  type AdminCreditPurchaseListQuery,
  type AdminCreditPurchaseSummaryQuery,
  type AdminCreditPurchaseMarkPaidRequest,
  type AdminUsersQuery,
  type ClaimAcceptRequest,
  type AdminCreatePostingRequest,
  type CreateWorkerExperienceRequest,
  type KycRejectRequest,
  type OrgImagePresignRequest,
  type PatchMeWorkerRequest,
  type CreditPackUpsertRequest,
  type AdminSubscriptionPlanUpsertRequest,
  type AdminPlanPurchaseListQuery,
  type AdminGiftCodeListQuery,
  type AdminCreateGiftCodesRequest,
  type UpdateOrgRequest,
  type AdminUpdatePostingRequest,
} from "@chotca/config";
import { z } from "zod";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

function buildQuery(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function createAdminResource(ctx: RequestContext) {
  return {
    dashboard() {
      return requestJson(ctx, {
        method: "GET",
        path: "/admin/dashboard",
        dataSchema: adminDashboardResponseSchema,
        auth: true,
      });
    },

    // Analytics (chart dashboard)
    analyticsSummary(query: AdminAnalyticsRangeQuery = {}) {
      const q = adminAnalyticsRangeQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/summary${buildQuery(q)}`,
        dataSchema: adminAnalyticsSummarySchema,
        auth: true,
      });
    },

    analyticsTimeseries(query: AdminAnalyticsRangeQuery = {}) {
      const q = adminAnalyticsRangeQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/timeseries${buildQuery(q)}`,
        dataSchema: adminAnalyticsTimeseriesSchema,
        auth: true,
      });
    },

    analyticsFunnel(query: AdminAnalyticsRangeQuery = {}) {
      const q = adminAnalyticsRangeQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/funnel${buildQuery(q)}`,
        dataSchema: adminAnalyticsFunnelSchema,
        auth: true,
      });
    },

    analyticsRevenue(query: AdminAnalyticsRangeQuery = {}) {
      const q = adminAnalyticsRangeQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/revenue${buildQuery(q)}`,
        dataSchema: adminAnalyticsRevenueSchema,
        auth: true,
      });
    },

    // Users
    listUsers(query: AdminUsersQuery = {}) {
      const q = adminUsersQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/users${buildQuery(q)}`,
        dataSchema: adminUsersListResponseSchema,
        auth: true,
      });
    },

    getUser(userId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/users/${userId}`,
        dataSchema: adminUserDetailSchema,
        auth: true,
      });
    },

    suspendUser(userId: string, body: { reason?: string } = {}) {
      const payload = adminSuspendUserRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/suspend`,
        body: payload,
        dataSchema: adminUserMutationResponseSchema,
        auth: true,
      });
    },

    activateUser(userId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/activate`,
        dataSchema: adminUserMutationResponseSchema,
        auth: true,
      });
    },

    // Grant an unclaimed shadow org to this (employer) account — it becomes the
    // org owner and the org stops being a "doanh nghiệp tạm".
    grantOrgToUser(userId: string, body: AdminGrantOrgRequest) {
      const payload = adminGrantOrgRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/grant-org`,
        body: payload,
        dataSchema: adminGrantOrgResponseSchema,
        auth: true,
      });
    },

    getUserContact(userId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/contact`,
        dataSchema: adminUserContactResponseSchema,
        auth: true,
      });
    },

    listUserSessions(userId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/users/${userId}/sessions`,
        dataSchema: adminSessionListResponseSchema,
        auth: true,
      });
    },

    revokeUserSession(userId: string, sessionId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/sessions/${sessionId}/revoke`,
        dataSchema: adminRevokeSessionResponseSchema,
        auth: true,
      });
    },

    revokeAllUserSessions(userId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/sessions/revoke-all`,
        dataSchema: adminRevokeSessionResponseSchema,
        auth: true,
      });
    },

    updateWorkerProfile(userId: string, body: PatchMeWorkerRequest) {
      const payload = patchMeWorkerRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/users/${userId}/worker`,
        body: payload,
        dataSchema: userMeSchema,
        auth: true,
      });
    },

    addWorkerExperience(userId: string, body: CreateWorkerExperienceRequest) {
      const payload = createWorkerExperienceRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/users/${userId}/worker/experience`,
        body: payload,
        dataSchema: workerExperienceSchema,
        auth: true,
      });
    },

    deleteWorkerExperience(userId: string, expId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/users/${userId}/worker/experience/${expId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // KYC review
    listWorkerKyc(query: AdminKycListQuery = {}) {
      const q = adminKycListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/kyc/workers${buildQuery(q)}`,
        dataSchema: adminWorkerKycListResponseSchema,
        auth: true,
      });
    },

    listOrgKyc(query: AdminOrgKycListQuery = {}) {
      const q = adminOrgKycListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/kyc/orgs${buildQuery(q)}`,
        dataSchema: adminOrgKycListResponseSchema,
        auth: true,
      });
    },

    getWorkerKyc(submissionId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/kyc/workers/${submissionId}`,
        dataSchema: workerKycSubmissionAdminSchema,
        auth: true,
      });
    },

    approveWorkerKyc(submissionId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/kyc/workers/${submissionId}/approve`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    rejectWorkerKyc(submissionId: string, body: KycRejectRequest) {
      const payload = kycRejectRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/kyc/workers/${submissionId}/reject`,
        body: payload,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    getOrgKycAdmin(submissionId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/kyc/orgs/${submissionId}`,
        dataSchema: orgKycSubmissionAdminSchema,
        auth: true,
      });
    },

    approveOrgKyc(submissionId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/kyc/orgs/${submissionId}/approve`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    rejectOrgKyc(submissionId: string, body: KycRejectRequest) {
      const payload = kycRejectRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/kyc/orgs/${submissionId}/reject`,
        body: payload,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // ADMIN override of an org's verify level (SCRUM-74).
    overrideOrgVerifyLevel(
      orgId: string,
      body: AdminOverrideVerifyLevelRequest,
    ) {
      const payload = adminOverrideVerifyLevelRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/verify-level`,
        body: payload,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Account identity (UserIdentity / CCCD) — SCRUM-71
    listIdentity(query: AdminKycListQuery = {}) {
      const q = adminKycListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/identity${buildQuery(q)}`,
        dataSchema: adminIdentityListResponseSchema,
        auth: true,
      });
    },

    getIdentity(userId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/identity/${userId}`,
        dataSchema: userIdentityAdminSchema,
        auth: true,
      });
    },

    approveIdentity(userId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/identity/${userId}/approve`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    rejectIdentity(userId: string, body: IdentityRejectRequest) {
      const payload = identityRejectRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/identity/${userId}/reject`,
        body: payload,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Orgs (admin)
    listOrgs(query: AdminOrgListQuery = {}) {
      const q = adminOrgListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs${buildQuery(q)}`,
        dataSchema: adminOrgListResponseSchema,
        auth: true,
      });
    },

    getOrg(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}`,
        dataSchema: adminOrgListItemSchema,
        auth: true,
      });
    },

    // Org interview calendar ("Lịch phỏng vấn") — admin concierge view.
    orgInterviews(orgId: string, query: InterviewCalendarQuery = {}) {
      const q = interviewCalendarQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}/interviews${buildQuery(q)}`,
        dataSchema: interviewCalendarResponseSchema,
        auth: true,
      });
    },

    // Cross-org contacts list ("Liên hệ") — invites by lifecycle status.
    listInvites(query: AdminInviteListQuery = {}) {
      const q = adminInviteListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/invites${buildQuery(q)}`,
        dataSchema: adminInviteListResponseSchema,
        auth: true,
      });
    },

    // Interview credit purchases (admin view)
    listCreditPurchases(query: AdminCreditPurchaseListQuery = {}) {
      const q = adminCreditPurchaseListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/interview-credit-purchases${buildQuery(q)}`,
        dataSchema: adminCreditPurchaseListResponseSchema,
        auth: true,
      });
    },

    creditPurchaseSummary(query: AdminCreditPurchaseSummaryQuery = {}) {
      const q = adminCreditPurchaseSummaryQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/interview-credit-purchases/summary${buildQuery(q)}`,
        dataSchema: adminCreditPurchaseSummarySchema,
        auth: true,
      });
    },

    getCreditPurchase(id: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/interview-credit-purchases/${id}`,
        dataSchema: adminCreditPurchaseDetailSchema,
        auth: true,
      });
    },

    syncCreditPurchase(id: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/interview-credit-purchases/${id}/sync`,
        dataSchema: adminCreditPurchaseActionResponseSchema,
        auth: true,
      });
    },

    markCreditPurchasePaid(
      id: string,
      body: AdminCreditPurchaseMarkPaidRequest,
    ) {
      const payload = adminCreditPurchaseMarkPaidRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/interview-credit-purchases/${id}/mark-paid`,
        body: payload,
        dataSchema: adminCreditPurchaseActionResponseSchema,
        auth: true,
      });
    },

    // Ratings moderation
    hideRating(ratingId: string, body: AdminHideRatingRequest) {
      const payload = adminHideRatingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/ratings/${ratingId}/hide`,
        body: payload,
        dataSchema: ratingSchema,
        auth: true,
      });
    },

    // Abuse
    listAbuseReports(
      query: { status?: string; page?: number; limit?: number } = {},
    ) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/abuse${buildQuery(query)}`,
        dataSchema: adminAbuseReportListResponseSchema,
        auth: true,
      });
    },

    assignSelfAbuse(reportId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/abuse/${reportId}/assign-self`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    resolveAbuse(reportId: string, body: AdminResolveAbuseRequest) {
      const payload = adminResolveAbuseRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/abuse/${reportId}/resolve`,
        body: payload,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Concierge / act-as employer
    grantCredits(orgId: string, amount: number, reason: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/grant-credits`,
        body: { amount, reason },
        dataSchema: z.object({ ok: z.literal(true), granted: z.number() }),
        auth: true,
      });
    },

    logActAs(
      orgId: string,
      action: string,
      metadata?: Record<string, unknown>,
    ) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/act-as-log`,
        body: { action, metadata },
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Cancellation requests (post-commit)
    listCancellationRequests(query: { status?: string; limit?: number } = {}) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/cancellation-requests${buildQuery(query)}`,
        dataSchema: cancellationRequestListResponseSchema,
        auth: true,
      });
    },

    resolveCancellationRequest(
      requestId: string,
      body: ResolveCancellationRequest,
    ) {
      const payload = resolveCancellationRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/cancellation-requests/${requestId}/resolve`,
        body: payload,
        dataSchema: z.object({
          id: z.string(),
          status: z.string(),
        }),
        auth: true,
      });
    },

    // Todo counts (admin todo dashboard)
    todoCounts() {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/todo`,
        dataSchema: adminTodoCountsSchema,
        auth: true,
      });
    },

    // Shadow orgs with pending applicants (concierge todo)
    listShadowOrgsWithApplicants(
      query: AdminShadowOrgsWithApplicantsQuery = {},
    ) {
      const q = adminShadowOrgsWithApplicantsQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/shadow-orgs-with-applicants${buildQuery(q)}`,
        dataSchema: adminShadowOrgsWithApplicantsResponseSchema,
        auth: true,
      });
    },

    // Unclaimed shadow orgs assignable to an employer account (grant picker).
    listAssignableShadowOrgs(query: AdminAssignableShadowOrgsQuery = {}) {
      const q = adminAssignableShadowOrgsQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/assignable-shadow-orgs${buildQuery(q)}`,
        dataSchema: adminAssignableShadowOrgListResponseSchema,
        auth: true,
      });
    },

    updateOrg(orgId: string, body: UpdateOrgRequest) {
      const payload = updateOrgRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/orgs/${orgId}`,
        body: payload,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    // Presign a gallery image upload for any org (admin). PUT the file to the
    // returned URL, then persist via `updateOrg({ galleryImageKeys })`.
    presignOrgImage(orgId: string, body: OrgImagePresignRequest) {
      const payload = orgImagePresignRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/images/presign`,
        body: payload,
        dataSchema: orgImagePresignResponseSchema,
        auth: true,
      });
    },

    // Org branches (work locations) — admin operates cross-org.
    listOrgAddresses(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}/addresses`,
        dataSchema: orgAddressListResponseSchema,
        auth: true,
      });
    },

    createOrgAddress(orgId: string, body: CreateOrgAddressRequest) {
      const payload = createOrgAddressRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/addresses`,
        body: payload,
        dataSchema: orgAddressSchema,
        auth: true,
      });
    },

    updateOrgAddress(addressId: string, body: UpdateOrgAddressRequest) {
      const payload = updateOrgAddressRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/org-addresses/${addressId}`,
        body: payload,
        dataSchema: orgAddressSchema,
        auth: true,
      });
    },

    archiveOrgAddress(addressId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/org-addresses/${addressId}/archive`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Org members (staff) — admin operates cross-org.
    listOrgMembers(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}/members`,
        dataSchema: z.object({ items: z.array(memberSchema) }),
        auth: true,
      });
    },

    addOrgMember(orgId: string, body: AddMemberRequest) {
      const payload = addMemberRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/members`,
        body: payload,
        dataSchema: memberSchema,
        auth: true,
      });
    },

    removeOrgMember(orgId: string, membershipId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/orgs/${orgId}/members/${membershipId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Shadow org create
    createShadowOrg(body: AdminCreateShadowOrgRequest) {
      const payload = adminCreateShadowOrgRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs`,
        body: payload,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    deactivateOrg(orgId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/deactivate`,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    activateOrg(orgId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/activate`,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    // Cross-org postings
    listPostings(query: AdminPostingListQuery = {}) {
      const q = adminPostingListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/postings${buildQuery(q)}`,
        dataSchema: adminPostingListResponseSchema,
        auth: true,
      });
    },

    getPosting(postingId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/postings/${postingId}`,
        dataSchema: adminPostingDetailSchema,
        auth: true,
      });
    },

    createPostingForOrg(orgId: string, body: AdminCreatePostingRequest) {
      const payload = adminCreatePostingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/postings`,
        body: payload,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    // Bulk-import scraped postings (resolves/creates orgs + branches).
    importPostings(body: AdminImportPostingsRequest) {
      const payload = adminImportPostingsRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/import/postings`,
        body: payload,
        dataSchema: adminImportPostingsResponseSchema,
        auth: true,
      });
    },

    updatePosting(postingId: string, body: AdminUpdatePostingRequest) {
      const payload = adminUpdatePostingRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/postings/${postingId}`,
        body: payload,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    closePosting(postingId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/postings/${postingId}/close`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    cancelPosting(postingId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/postings/${postingId}/cancel`,
        dataSchema: postingSchema,
        auth: true,
      });
    },

    deletePosting(postingId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/postings/${postingId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    listApplicants(
      postingId: string,
      query: { cursor?: string; limit?: number } = {},
    ) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/postings/${postingId}/applications${buildQuery(query)}`,
        dataSchema: applicantListResponseSchema,
        auth: true,
      });
    },

    getApplicantProfile(applicationId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/applications/${applicationId}/profile`,
        dataSchema: applicantProfileSchema,
        auth: true,
      });
    },

    inviteApplication(applicationId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/applications/${applicationId}/invite`,
        dataSchema: inviteApplicationResponseSchema,
        auth: true,
      });
    },

    rejectApplication(applicationId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/applications/${applicationId}/reject`,
        dataSchema: applicationSchema,
        auth: true,
      });
    },

    deleteApplication(applicationId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/applications/${applicationId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Claim invitations
    listAllClaimInvitations(query: AdminClaimInvitationListQuery = {}) {
      const q = adminClaimInvitationListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/claim-invitations${buildQuery(q)}`,
        dataSchema: adminClaimInvitationListResponseSchema,
        auth: true,
      });
    },

    listClaimInvitations(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}/claim-invitations`,
        dataSchema: orgClaimInvitationListResponseSchema,
        auth: true,
      });
    },

    createClaimInvitation(
      orgId: string,
      body: AdminCreateClaimInvitationRequest,
    ) {
      const payload = adminCreateClaimInvitationRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/claim-invitations`,
        body: payload,
        dataSchema: orgClaimInvitationSchema,
        auth: true,
      });
    },

    revokeClaimInvitation(invitationId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/claim-invitations/${invitationId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // Public claim flow (no auth) — exposed under admin namespace for codegen convenience.
    claimPreview(token: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/claim/${token}/preview`,
        dataSchema: claimPreviewResponseSchema,
        auth: false,
      });
    },

    claimAccept(token: string, body: ClaimAcceptRequest) {
      const payload = claimAcceptRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/claim/${token}/accept`,
        body: payload,
        dataSchema: claimAcceptResponseSchema,
        auth: false,
      });
    },

    // Credit packs (pricing config CRUD)
    listCreditPacks() {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/credit-packs`,
        dataSchema: creditPackListResponseSchema,
        auth: true,
      });
    },

    upsertCreditPack(body: CreditPackUpsertRequest) {
      const payload = creditPackUpsertRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/credit-packs`,
        body: payload,
        dataSchema: creditPackSchema,
        auth: true,
      });
    },

    deleteCreditPack(packCode: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/credit-packs/${encodeURIComponent(packCode)}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },

    // -------- Subscription plans + purchases --------
    listSubscriptionPlans() {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/subscription-plans`,
        dataSchema: adminSubscriptionPlanListResponseSchema,
        auth: true,
      });
    },

    upsertSubscriptionPlan(body: AdminSubscriptionPlanUpsertRequest) {
      const payload = adminSubscriptionPlanUpsertRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/subscription-plans`,
        body: payload,
        dataSchema: subscriptionPlanSchema,
        auth: true,
      });
    },

    listPlanPurchases(query: AdminPlanPurchaseListQuery = {}) {
      const q = adminPlanPurchaseListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/plan-purchases${buildQuery(q)}`,
        dataSchema: adminPlanPurchaseListResponseSchema,
        auth: true,
      });
    },

    subscriptionStats() {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/subscriptions`,
        dataSchema: adminSubscriptionStatsSchema,
        auth: true,
      });
    },

    // Orgs behind the "Pro từ mã / từ mua" dashboard stats (the (i) drill-down).
    proSourceOrgs(source: "GIFT" | "PURCHASE") {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/analytics/pro-source-orgs?source=${source}`,
        dataSchema: adminProSourceOrgsResponseSchema,
        auth: true,
      });
    },

    // -------- Plan gift codes ("mã quà tặng") --------
    listGiftCodes(query: AdminGiftCodeListQuery = {}) {
      const q = adminGiftCodeListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/gift-codes${buildQuery(q)}`,
        dataSchema: adminGiftCodeListResponseSchema,
        auth: true,
      });
    },

    createGiftCodes(body: AdminCreateGiftCodesRequest) {
      const payload = adminCreateGiftCodesRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/gift-codes`,
        body: payload,
        dataSchema: adminCreateGiftCodesResponseSchema,
        auth: true,
      });
    },

    revokeGiftCode(id: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/gift-codes/${id}/revoke`,
        dataSchema: planGiftCodeSchema,
        auth: true,
      });
    },

    // Current plan status for an org (admin view).
    getPlanStatus(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/orgs/${orgId}/plan`,
        dataSchema: orgPlanStatusSchema,
        auth: true,
      });
    },

    // Concierge: grant/renew an org's plan.
    subscribeOrg(orgId: string, planCode: "FREE" | "PRO" | "BUSINESS") {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/orgs/${orgId}/subscribe`,
        body: { planCode },
        dataSchema: orgPlanStatusSchema,
        auth: true,
      });
    },

    // Activity log (audit)
    listActivityLog(query: ActivityLogQuery = {}) {
      const q = activityLogQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/activity${buildQuery(q)}`,
        dataSchema: activityLogListResponseSchema,
        auth: true,
      });
    },

    // Notifications — custom push + template management
    sendNotification(body: AdminSendNotificationRequest) {
      const payload = adminSendNotificationRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/admin/notifications/send",
        body: payload,
        dataSchema: adminSendNotificationResponseSchema,
        auth: true,
      });
    },

    listNotificationTemplates() {
      return requestJson(ctx, {
        method: "GET",
        path: "/admin/notifications/templates",
        dataSchema: adminNotificationTemplateListResponseSchema,
        auth: true,
      });
    },

    getNotificationTemplate(type: NotificationTypeValue) {
      return requestJson(ctx, {
        method: "GET",
        path: `/admin/notifications/templates/${encodeURIComponent(type)}`,
        dataSchema: notificationTemplateSchema,
        auth: true,
      });
    },

    updateNotificationTemplate(
      type: NotificationTypeValue,
      body: AdminUpdateNotificationTemplateRequest,
    ) {
      const payload = adminUpdateNotificationTemplateRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/notifications/templates/${encodeURIComponent(type)}`,
        body: payload,
        dataSchema: notificationTemplateSchema,
        auth: true,
      });
    },

    resetNotificationTemplate(type: NotificationTypeValue) {
      return requestJson(ctx, {
        method: "POST",
        path: `/admin/notifications/templates/${encodeURIComponent(type)}/reset`,
        dataSchema: notificationTemplateSchema,
        auth: true,
      });
    },

    // System posting templates (the "Mẫu hệ thống" admin manages — they
    // power the "Bắt đầu nhanh" picker on the employer post form).
    listSystemPostingTemplates() {
      return requestJson(ctx, {
        method: "GET",
        path: "/admin/posting-templates",
        dataSchema: postingTemplateListResponseSchema,
        auth: true,
      });
    },

    createSystemPostingTemplate(body: AdminSystemPostingTemplateUpsertRequest) {
      const payload = adminSystemPostingTemplateUpsertSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/admin/posting-templates",
        body: payload,
        dataSchema: postingTemplateSchema,
        auth: true,
      });
    },

    updateSystemPostingTemplate(
      templateId: string,
      body: AdminSystemPostingTemplateUpsertRequest,
    ) {
      const payload = adminSystemPostingTemplateUpsertSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/admin/posting-templates/${templateId}`,
        body: payload,
        dataSchema: postingTemplateSchema,
        auth: true,
      });
    },

    deleteSystemPostingTemplate(templateId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/admin/posting-templates/${templateId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },
  };
}
